import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Building2,
  Calendar,
  ChevronRight,
  Globe,
  Package,
  Pause,
  Play,
  Plus,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { organizationsApi } from '@/api';
import { CopyId, StatusPill } from '@/components/page';
import { ConfirmDialog } from '@/components/admin-ui';
import { useCrumbLabel } from '@/components/breadcrumbs';
import { EmptyState, MarkTile, Notice, ago, fmtDate, plural, rowLink } from '@/components/workspace-ui';
import {
  useOrganization,
  useUpdateOrganization,
  useDeleteOrganization,
  useOrganizationMembers,
  useAddOrganizationMember,
  useRemoveOrganizationMember,
  useUpdateOrganizationMemberRole,
  useProducts,
  useClients,
  useToast,
  useAuth,
} from '@/hooks';
import type { OrganizationMember, OrganizationRole } from '@/types';
import { cn } from '@/lib/utils';

const ASSIGNABLE: OrganizationRole[] = ['admin', 'member', 'viewer'];
const ROLE_INFO: Record<OrganizationRole, string> = {
  owner: 'Full control, including deleting the organization.',
  admin: 'Manage members, apps and settings.',
  member: 'Use the organization’s apps and see its members.',
  viewer: 'Read-only access to the organization.',
};
const ACCESS_LABEL: Record<string, string> = {
  gated: 'Gated',
  org_members: 'Org members',
  open: 'Open',
};
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type Tab = 'overview' | 'members' | 'apps' | 'settings';

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

function validUrl(v: string): boolean {
  if (!v) return true;
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function Card({
  title,
  sub,
  action,
  children,
  className,
}: {
  title: string;
  sub?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('overflow-hidden rounded-lg border border-border bg-card', className)}>
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3.5">
        <div className="min-w-0">
          <h2 className="text-[14.5px] font-semibold">{title}</h2>
          {sub && <p className="mt-0.5 text-[12.5px] text-muted-foreground">{sub}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function RoleTag({ role }: { role: string }) {
  return (
    <span className="rounded-[5px] border border-border px-1.5 py-0.5 text-[11.5px] text-muted-foreground">
      {role}
    </span>
  );
}

function Field({
  id,
  label,
  optional,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  error?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {optional && <span className="ml-1.5 text-xs font-normal text-muted-foreground">optional</span>}
      </Label>
      {children}
      {error ? (
        <p id={`${id}-err`} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.is_super_admin === true;

  const { data: org, isLoading, isError, error: loadError, refetch } = useOrganization(id);
  const { data: members = [], isLoading: membersLoading } = useOrganizationMembers(id);
  const { data: apps = [], isLoading: appsLoading } = useProducts(id);
  const { data: clients = [] } = useClients();
  const updateOrg = useUpdateOrganization();
  const deleteOrg = useDeleteOrganization();
  const addMember = useAddOrganizationMember();
  const removeMember = useRemoveOrganizationMember();
  const updateMemberRole = useUpdateOrganizationMemberRole();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [removing, setRemoving] = useState<OrganizationMember | null>(null);
  const [toggleOpen, setToggleOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useCrumbLabel(org?.name);
  useEffect(() => {
    if (org) document.title = `${org.name} · authrz.me`;
  }, [org]);

  const canManage = isSuperAdmin || org?.role === 'owner' || org?.role === 'admin';
  const canDelete = isSuperAdmin || org?.role === 'owner';

  const defaultTab: Tab = location.pathname.endsWith('/edit') ? 'settings' : 'overview';
  const requested = (searchParams.get('tab') as Tab | null) ?? defaultTab;
  const tab: Tab = requested === 'settings' && !canManage ? 'overview' : requested;
  const setTab = (t: string) => {
    const next = new URLSearchParams(searchParams);
    if (t === 'overview') next.delete('tab');
    else next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  const clientCount = useMemo(() => {
    const m = new Map<string, number>();
    clients.forEach((c) => m.set(c.product_id, (m.get(c.product_id) ?? 0) + 1));
    return m;
  }, [clients]);

  const owners = members.filter((m) => m.role === 'owner').length;

  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 rounded-xl" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-56" />
            <Skeleton className="h-4 w-40" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !org) {
    const msg = loadError instanceof Error ? loadError.message : '';
    const notFound = !isError || /not found|404/i.test(msg);
    return (
      <div className="rounded-lg border border-border bg-card">
        <EmptyState
          icon={Building2}
          title={notFound ? 'Organization not found' : 'Couldn’t load this organization'}
          body={
            notFound
              ? 'It may have been deleted, or you may not have access to it.'
              : 'Check your connection and try again.'
          }
          action={
            <div className="flex gap-2">
              {!notFound && (
                <Button variant="secondary" onClick={() => refetch()}>
                  Retry
                </Button>
              )}
              <Button asChild variant="secondary">
                <Link to="/organizations">Back to organizations</Link>
              </Button>
            </div>
          }
        />
      </div>
    );
  }

  const toastError = (title: string, e: unknown, fallback: string) =>
    toast({ variant: 'destructive', title, description: e instanceof Error ? e.message : fallback });

  const handleRoleChange = async (m: OrganizationMember, role: OrganizationRole) => {
    try {
      await updateMemberRole.mutateAsync({ organizationId: org.id, userId: m.user_id, role });
      toast({ title: 'Role updated', description: `${m.user?.name || m.user?.email || 'Member'} is now ${role}.` });
    } catch (e) {
      toastError('Update failed', e, 'Failed to update role');
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    try {
      await removeMember.mutateAsync({ organizationId: org.id, userId: removing.user_id });
      toast({ title: 'Member removed', description: `${removing.user?.name || removing.user?.email} is no longer in ${org.name}.` });
    } catch (e) {
      toastError('Remove failed', e, 'Failed to remove member');
    } finally {
      setRemoving(null);
    }
  };

  const handleToggleActive = async () => {
    const off = org.is_active;
    try {
      await updateOrg.mutateAsync({ id: org.id, data: { isActive: !off } });
      toast({ title: off ? 'Organization deactivated' : 'Organization reactivated', description: org.name });
    } catch (e) {
      toastError('Update failed', e, 'Failed to update status');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOrg.mutateAsync(org.id);
      toast({ title: 'Organization deleted', description: `${org.name} was removed.` });
      navigate('/organizations');
    } catch (e) {
      toastError('Delete failed', e, 'Failed to delete organization');
    }
  };

  const selfRemoval = removing?.user_id === user?.id;
  const removeName = removing?.user?.name || removing?.user?.email || 'this member';

  return (
    <div>
      {!org.is_active && (
        <Notice
          tone="warn"
          className="mb-4"
          title="This organization is deactivated"
          action={
            canManage ? (
              <Button variant="secondary" size="sm" onClick={() => setTab('settings')}>
                Review settings
              </Button>
            ) : undefined
          }
        >
          Members keep their accounts, but the organization is hidden from sign-in choices until it’s reactivated.
        </Notice>
      )}

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3.5">
          <MarkTile name={org.name} size="lg" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="truncate text-2xl font-semibold leading-tight tracking-[-0.03em]">{org.name}</h1>
              <StatusPill tone={org.is_active ? 'ok' : 'neutral'}>{org.is_active ? 'Active' : 'Inactive'}</StatusPill>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-muted-foreground">
              <CopyId value={org.slug} />
              {org.website && (
                <a
                  href={org.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 hover:text-foreground hover:underline"
                >
                  <Globe className="h-3.5 w-3.5" />
                  {hostOf(org.website)}
                </a>
              )}
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                {plural(members.length, 'member')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                Created {fmtDate(org.created_at)}
              </span>
            </div>
          </div>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setTab('members');
                setInviteOpen(true);
              }}
            >
              <UserPlus />
              Add member
            </Button>
            {isSuperAdmin && (
              <Button asChild>
                <Link to={`/products/new?org=${encodeURIComponent(org.id)}`}>
                  <Plus />
                  New app
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full overflow-x-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="members">
            Members
            <span className="font-mono text-[11px] text-muted-foreground">{members.length}</span>
          </TabsTrigger>
          <TabsTrigger value="apps">
            Apps
            <span className="font-mono text-[11px] text-muted-foreground">{apps.length}</span>
          </TabsTrigger>
          {canManage && <TabsTrigger value="settings">Settings</TabsTrigger>}
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="grid gap-5 lg:grid-cols-2">
            <Card title="Details" sub="Identifiers used by the API and in tokens.">
              <dl className="grid grid-cols-[120px_1fr] items-center gap-x-4 gap-y-3.5 px-4 py-4 text-[13.5px]">
                <dt className="text-muted-foreground">Organization ID</dt>
                <dd className="min-w-0"><CopyId value={org.id} /></dd>
                <dt className="text-muted-foreground">Slug</dt>
                <dd className="min-w-0"><CopyId value={org.slug} /></dd>
                <dt className="text-muted-foreground">Website</dt>
                <dd className="min-w-0 break-all">
                  {org.website ? (
                    <a href={org.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {org.website}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">Not set</span>
                  )}
                </dd>
                <dt className="text-muted-foreground">Status</dt>
                <dd>
                  <StatusPill tone={org.is_active ? 'ok' : 'neutral'}>{org.is_active ? 'Active' : 'Inactive'}</StatusPill>
                </dd>
                <dt className="text-muted-foreground">Your role</dt>
                <dd className="flex flex-wrap items-center gap-2">
                  {org.role ? (
                    <>
                      <RoleTag role={org.role} />
                      <span className="text-xs text-muted-foreground">{ROLE_INFO[org.role]}</span>
                    </>
                  ) : isSuperAdmin ? (
                    <>
                      <RoleTag role="super admin" />
                      <span className="text-xs text-muted-foreground">Full access to every organization.</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </dd>
                <dt className="text-muted-foreground">Created</dt>
                <dd className="font-mono text-[12.5px]">{fmtDate(org.created_at)}</dd>
                <dt className="self-start text-muted-foreground">Description</dt>
                <dd className={cn(!org.description && 'text-muted-foreground')}>
                  {org.description || 'No description.'}
                </dd>
              </dl>
            </Card>

            <Card title="Apps" sub="Resources this organization owns.">
              {appsLoading ? (
                <div className="space-y-3 p-4">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : apps.length ? (
                <ul className="divide-y divide-border">
                  {apps.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/products/${p.id}`}
                        className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-foreground/[0.03]"
                      >
                        <MarkTile name={p.name} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-semibold">{p.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {ACCESS_LABEL[p.settings?.access_model ?? 'gated']} · {plural(clientCount.get(p.id) ?? 0, 'client')}
                          </p>
                        </div>
                        {!p.is_active && <StatusPill>Inactive</StatusPill>}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState
                  icon={Package}
                  title="No apps yet"
                  body="Apps hold the roles and OAuth clients your users sign in to."
                  action={
                    isSuperAdmin ? (
                      <Button asChild variant="secondary">
                        <Link to={`/products/new?org=${encodeURIComponent(org.id)}`}>
                          <Plus />
                          New app
                        </Link>
                      </Button>
                    ) : undefined
                  }
                />
              )}
            </Card>
          </div>
        </TabsContent>

        {/* Members */}
        <TabsContent value="members">
          <Card
            title="Members"
            sub={
              canManage
                ? 'Change roles inline. The owner role can’t be reassigned or removed from the last owner.'
                : 'People in this organization and their roles.'
            }
            action={
              canManage ? (
                <Button variant="secondary" size="sm" onClick={() => setInviteOpen(true)}>
                  <UserPlus />
                  Add member
                </Button>
              ) : undefined
            }
          >
            {membersLoading ? (
              <div className="space-y-3 p-4">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            ) : members.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No members"
                body="Add people by the email they use to sign in to authrz.me."
                action={
                  canManage ? (
                    <Button variant="secondary" onClick={() => setInviteOpen(true)}>
                      <UserPlus />
                      Add member
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="hidden sm:table-cell">Joined</TableHead>
                    {canManage && (
                      <TableHead className="w-10">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m) => {
                    const label = m.user?.name || m.user?.email || m.user_id;
                    const href = isSuperAdmin && m.user?.id ? `/users/${m.user.id}` : undefined;
                    const self = m.user_id === user?.id;
                    const lastOwner = m.role === 'owner' && owners <= 1;
                    const identity = (
                      <span className="flex min-w-0 flex-col">
                        <b className="truncate font-semibold">
                          {label}
                          {self && <span className="ml-1 text-xs font-normal text-muted-foreground">(you)</span>}
                        </b>
                        <span className="truncate text-xs text-muted-foreground" title={m.user?.email}>
                          {m.user?.email ?? '—'}
                        </span>
                      </span>
                    );
                    return (
                      <TableRow
                        key={m.user_id}
                        className={href ? rowLink : undefined}
                        onClick={
                          href
                            ? (e) => {
                                if (!(e.target as HTMLElement).closest('a,button,[role=combobox]')) navigate(href);
                              }
                            : undefined
                        }
                      >
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <span
                              aria-hidden="true"
                              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground/[0.06] text-[11.5px] font-semibold uppercase"
                            >
                              {(m.user?.name || m.user?.email || '?').slice(0, 2)}
                            </span>
                            {href ? <Link to={href}>{identity}</Link> : identity}
                          </div>
                        </TableCell>
                        <TableCell>
                          {canManage && m.role !== 'owner' ? (
                            <Select value={m.role} onValueChange={(v) => handleRoleChange(m, v as OrganizationRole)}>
                              <SelectTrigger className="h-8 w-[120px]" aria-label={`Role for ${label}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ASSIGNABLE.map((r) => (
                                  <SelectItem key={r} value={r}>
                                    {r}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <RoleTag role={m.role} />
                          )}
                        </TableCell>
                        <TableCell className="hidden whitespace-nowrap font-mono text-xs text-muted-foreground sm:table-cell">
                          {fmtDate(m.joined_at)}
                        </TableCell>
                        {canManage && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setRemoving(m)}
                              disabled={lastOwner}
                              title={lastOwner ? 'An organization needs at least one owner' : undefined}
                              aria-label={`Remove ${label}`}
                            >
                              <Trash2 />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Apps */}
        <TabsContent value="apps">
          <Card
            title="Apps"
            sub="Each app owns its roles, grants and OAuth clients."
            action={
              isSuperAdmin ? (
                <Button asChild variant="secondary" size="sm">
                  <Link to={`/products/new?org=${encodeURIComponent(org.id)}`}>
                    <Plus />
                    New app
                  </Link>
                </Button>
              ) : undefined
            }
          >
            {appsLoading ? (
              <div className="space-y-3 p-4">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            ) : apps.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>App</TableHead>
                    <TableHead>Access</TableHead>
                    <TableHead className="text-right">Clients</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden sm:table-cell">Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apps.map((p) => {
                    const href = `/products/${p.id}`;
                    return (
                      <TableRow
                        key={p.id}
                        className={cn(rowLink, !p.is_active && 'opacity-60')}
                        onClick={(e) => {
                          if (!(e.target as HTMLElement).closest('a,button')) navigate(href);
                        }}
                      >
                        <TableCell>
                          <Link to={href} className="flex items-center gap-2.5">
                            <MarkTile name={p.name} />
                            <span className="flex min-w-0 flex-col">
                              <b className="max-w-[240px] truncate font-semibold" title={p.name}>{p.name}</b>
                              <span className="font-mono text-xs text-muted-foreground">{p.slug}</span>
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <RoleTag role={ACCESS_LABEL[p.settings?.access_model ?? 'gated']} />
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{clientCount.get(p.id) ?? 0}</TableCell>
                        <TableCell>
                          <StatusPill tone={p.is_active ? 'ok' : 'neutral'}>{p.is_active ? 'Active' : 'Inactive'}</StatusPill>
                        </TableCell>
                        <TableCell className="hidden whitespace-nowrap font-mono text-xs text-muted-foreground sm:table-cell">
                          {ago(p.updated_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <EmptyState
                icon={Package}
                title="No apps yet"
                body="Create an app to define roles and register OAuth clients."
                action={
                  isSuperAdmin ? (
                    <Button asChild variant="secondary">
                      <Link to={`/products/new?org=${encodeURIComponent(org.id)}`}>
                        <Plus />
                        New app
                      </Link>
                    </Button>
                  ) : undefined
                }
              />
            )}
          </Card>
        </TabsContent>

        {/* Settings */}
        {canManage && (
          <TabsContent value="settings">
            <div className="space-y-5">
              <SettingsForm
                key={`${org.id}-${org.updated_at}`}
                org={org}
                saving={updateOrg.isPending}
                onSave={async (data) => {
                  const changed = (Object.keys(data) as (keyof typeof data)[]).filter(
                    (k) => (org[k as 'name'] ?? '') !== data[k]
                  );
                  if (!changed.length) {
                    toast({ title: 'No changes to save' });
                    return;
                  }
                  await updateOrg.mutateAsync({ id: org.id, data });
                  toast({ title: 'Organization saved', description: `Updated ${changed.join(', ')}.` });
                }}
              />

              <section className="overflow-hidden rounded-lg border border-destructive/40 bg-card">
                <div className="border-b border-destructive/30 px-4 py-3.5">
                  <h2 className="text-[14.5px] font-semibold">Danger zone</h2>
                  <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                    These actions affect every member and app in {org.name}.
                  </p>
                </div>
                <div className="divide-y divide-border">
                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
                    <div className="min-w-0 max-w-[60ch]">
                      <b>{org.is_active ? 'Deactivate organization' : 'Reactivate organization'}</b>
                      <p className="mt-0.5 text-[13px] text-muted-foreground">
                        {!isSuperAdmin
                          ? 'Only a super admin can change an organization’s status.'
                          : org.is_active
                            ? 'Hides the organization from sign-in choices. Members, apps and clients are kept and can be restored.'
                            : 'Restores the organization for its members and apps.'}
                      </p>
                    </div>
                    <Button
                      variant={org.is_active ? 'outline' : 'secondary'}
                      className={cn(org.is_active && 'text-destructive hover:text-destructive')}
                      disabled={!isSuperAdmin}
                      onClick={() => setToggleOpen(true)}
                    >
                      {org.is_active ? <Pause /> : <Play />}
                      {org.is_active ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
                    <div className="min-w-0 max-w-[60ch]">
                      <b>Delete organization</b>
                      <p className="mt-0.5 text-[13px] text-muted-foreground">
                        {apps.length
                          ? `Delete or move its ${plural(apps.length, 'app')} first. Deleting is permanent.`
                          : canDelete
                            ? 'Permanently removes the organization and its memberships. This can’t be undone.'
                            : 'Only an owner can delete this organization.'}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      disabled={apps.length > 0 || !canDelete}
                      onClick={() => setDeleteOpen(true)}
                    >
                      <Trash2 />
                      Delete
                    </Button>
                  </div>
                </div>
              </section>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <AddMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        orgName={org.name}
        existingEmails={members.map((m) => (m.user?.email ?? '').toLowerCase())}
        pending={addMember.isPending}
        onAdd={async (email, role) => {
          const added = await addMember.mutateAsync({ organizationId: org.id, email, role });
          toast({
            title: 'Member added',
            description: `${added.user?.name || email} joined ${org.name} as ${role}.`,
          });
        }}
      />

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={selfRemoval ? 'Leave this organization?' : `Remove ${removeName}?`}
        body={
          <>
            {selfRemoval ? 'You' : removeName} will lose the <b>{removing?.role}</b> role in {org.name} and access to
            apps that rely on organization membership. Explicit app grants are kept.
          </>
        }
        confirmLabel={selfRemoval ? 'Leave organization' : 'Remove member'}
        danger
        onConfirm={handleRemove}
      />

      <ConfirmDialog
        open={toggleOpen}
        onOpenChange={setToggleOpen}
        title={org.is_active ? `Deactivate ${org.name}?` : `Reactivate ${org.name}?`}
        body={
          org.is_active
            ? 'Members keep their accounts and app grants, but the organization is hidden until reactivated.'
            : 'The organization becomes available to its members again.'
        }
        confirmLabel={org.is_active ? 'Deactivate' : 'Reactivate'}
        danger={org.is_active}
        onConfirm={handleToggleActive}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete ${org.name}?`}
        body={`This permanently removes the organization and its ${plural(members.length, 'membership')}. Audit events are kept.`}
        confirmLabel="Delete organization"
        danger
        typeToConfirm={org.slug}
        onConfirm={handleDelete}
      />
    </div>
  );
}

function SettingsForm({
  org,
  saving,
  onSave,
}: {
  org: { id: string; name: string; slug: string; website: string | null; description: string | null };
  saving: boolean;
  onSave: (data: { name: string; slug: string; website: string; description: string }) => Promise<void>;
}) {
  const initial = {
    name: org.name,
    slug: org.slug,
    website: org.website ?? '',
    description: org.description ?? '',
  };
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState<{ name?: string; slug?: string; website?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const set = (k: keyof typeof form, v: string) => setForm((p) => ({ ...p, [k]: v }));
  const dirty = (Object.keys(initial) as (keyof typeof initial)[]).some((k) => initial[k] !== form[k]);

  const checkSlug = async (): Promise<string | undefined> => {
    const v = form.slug.trim();
    if (!v) return 'Enter a slug.';
    if (!SLUG_RE.test(v)) return 'Use lowercase letters, numbers and single hyphens.';
    if (v === org.slug) return undefined;
    try {
      const r = await organizationsApi.checkSlug(v, org.id);
      if (!r.available) return 'Another organization already uses this slug.';
    } catch {
      /* server re-checks on save */
    }
    return undefined;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const next = {
      name: form.name.trim() ? undefined : 'Enter a name.',
      slug: await checkSlug(),
      website: validUrl(form.website.trim()) ? undefined : 'Enter a full URL starting with https://',
    };
    setErrors(next);
    if (next.name || next.slug || next.website) {
      document.getElementById(next.name ? 'f-name' : next.slug ? 'f-slug' : 'f-web')?.focus();
      return;
    }
    try {
      await onSave({
        name: form.name.trim(),
        slug: form.slug.trim(),
        website: form.website.trim(),
        description: form.description.trim(),
      });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save organization');
    }
  };

  const errCls = (k: 'name' | 'slug' | 'website') => cn(errors[k] && 'border-destructive');

  return (
    <form onSubmit={submit} noValidate className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border px-4 py-3.5">
        <h2 className="text-[14.5px] font-semibold">Organization details</h2>
        <p className="mt-0.5 text-[12.5px] text-muted-foreground">
          Changing the slug breaks links and API calls that use the old one.
        </p>
      </div>
      <div className="grid gap-4 px-4 py-5 sm:grid-cols-2">
        <Field id="f-name" label="Name" error={errors.name}>
          <Input
            id="f-name"
            value={form.name}
            maxLength={80}
            required
            aria-invalid={!!errors.name}
            className={errCls('name')}
            onChange={(e) => set('name', e.target.value)}
          />
        </Field>
        <Field id="f-slug" label="Slug" error={errors.slug} hint="Lowercase letters, numbers and hyphens.">
          <Input
            id="f-slug"
            value={form.slug}
            maxLength={48}
            required
            spellCheck={false}
            autoCapitalize="off"
            aria-invalid={!!errors.slug}
            className={cn('font-mono', errCls('slug'))}
            onChange={(e) => set('slug', e.target.value)}
            onBlur={() => set('slug', slugify(form.slug) || form.slug.trim())}
          />
        </Field>
        <div className="sm:col-span-2">
          <Field id="f-web" label="Website" optional error={errors.website}>
            <Input
              id="f-web"
              type="url"
              inputMode="url"
              value={form.website}
              placeholder="https://example.com"
              aria-invalid={!!errors.website}
              className={errCls('website')}
              onChange={(e) => set('website', e.target.value)}
            />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field id="f-desc" label="Description" optional>
            <Textarea
              id="f-desc"
              value={form.description}
              maxLength={280}
              placeholder="What this organization does"
              onChange={(e) => set('description', e.target.value)}
            />
          </Field>
        </div>
        {formError && (
          <div className="sm:col-span-2">
            <Notice tone="bad" title="Couldn’t save changes">
              {formError}
            </Notice>
          </div>
        )}
      </div>
      <div className="flex flex-col-reverse gap-2 border-t border-border px-4 py-3 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="secondary"
          disabled={!dirty || saving}
          onClick={() => {
            setForm(initial);
            setErrors({});
            setFormError(null);
          }}
        >
          Discard changes
        </Button>
        <Button type="submit" disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

function AddMemberDialog({
  open,
  onOpenChange,
  orgName,
  existingEmails,
  pending,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgName: string;
  existingEmails: string[];
  pending: boolean;
  onAdd: (email: string, role: OrganizationRole) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrganizationRole>('member');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEmail('');
      setRole('member');
      setError(null);
    }
  }, [open]);

  const validate = (): string | null => {
    const v = email.trim().toLowerCase();
    if (!v) return 'Enter an email address.';
    if (!EMAIL_RE.test(v)) return 'Enter a valid email address, like person@example.com.';
    if (existingEmails.includes(v)) return 'This person is already a member.';
    return null;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    setError(err);
    if (err) return;
    try {
      await onAdd(email.trim().toLowerCase(), role);
      onOpenChange(false);
    } catch (e2) {
      const msg = e2 instanceof Error ? e2.message : '';
      if (/not found/i.test(msg)) {
        setError('No authrz.me account uses this email. Ask them to register first, then add them.');
      } else if (/another organization/i.test(msg)) {
        setError('This person already belongs to another organization. A super admin can move them from the user page.');
      } else {
        setError(msg || 'Failed to add member');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !pending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>Add member to {orgName}</DialogTitle>
            <DialogDescription>
              They need an authrz.me account. Their organization role applies to every app that uses organization
              membership.
            </DialogDescription>
          </DialogHeader>
          <Field id="inv-email" label="Email" error={error ?? undefined}>
            <Input
              id="inv-email"
              type="email"
              autoComplete="off"
              autoFocus
              placeholder="person@example.com"
              value={email}
              aria-invalid={!!error}
              aria-describedby={error ? 'inv-email-err' : undefined}
              className={cn(error && 'border-destructive')}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
            />
          </Field>
          <div className="space-y-1.5">
            <Label htmlFor="inv-role">Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as OrganizationRole)}>
              <SelectTrigger id="inv-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ASSIGNABLE.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{ROLE_INFO[role]}</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" disabled={pending} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              <UserPlus />
              {pending ? 'Adding…' : 'Add member'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
